import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Compass, Medal, Handshake, Phone } from "@phosphor-icons/react";
import Newsletter from "@/components/Newsletter";
import { SITE, waLink } from "@/lib/site";

const VALUES = [
    { icon: Heart, title: "Human first", text: "We remember your coffee order by day three." },
    { icon: Compass, title: "Deep local access", text: "Rooms, tables and guides most travelers never see." },
    { icon: Medal, title: "Craftsmanship", text: "Every itinerary is built by hand, never cloned." },
    { icon: Handshake, title: "Honest pricing", text: "No hidden markups. Just fair, considered value." },
];

const TEAM = [
    {
        name: "Priya Sharma",
        role: "Founder & Lead Concierge",
        bio: "Former luxury hotel director with 15 years across Asia and Europe. Priya started Midasis after one too many 'cookie-cutter' itineraries.",
        image: "https://images.unsplash.com/photo-1623594675959-02360202d4d6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzc2MzQ4MjY0fDA&ixlib=rb-4.1.0&q=85",
        destinations: ["Maldives", "Japan", "France"],
    },
    {
        name: "Arjun Mehta",
        role: "Europe & Americas Specialist",
        bio: "Spent three years living in Barcelona and Paris. Arjun's restaurant lists are legendary among our clients.",
        image: "https://images.pexels.com/photos/17582358/pexels-photo-17582358.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
        destinations: ["Spain", "Italy", "USA"],
    },
    {
        name: "Kavya Reddy",
        role: "Asia & Islands Expert",
        bio: "Kavya has personally stayed in over 60 properties across Southeast Asia. She knows which overwater villa faces the best sunset.",
        image: "https://images.unsplash.com/photo-1750187655549-b5009f762677?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMHNtaWxpbmd8ZW58MHx8fHwxNzc2MzQ4MjY0fDA&ixlib=rb-4.1.0&q=85",
        destinations: ["Bali", "Maldives", "Thailand"],
    },
];

const MILESTONES = [
    { year: "2013", event: "Founded in a small Hyderabad studio with 3 people and a big idea." },
    { year: "2015", event: "Crossed 500 happy travelers. Expanded to Europe and Southeast Asia." },
    { year: "2018", event: "Became IATA-affiliated. Launched our 24/7 concierge service." },
    { year: "2021", event: "Navigated the pandemic — rebooked 400+ trips, zero cancellation fees charged." },
    { year: "2024", event: "4,800+ travelers. 60+ destinations. Still answering our own phones." },
];

export default function About() {
    return (
        <div data-testid="page-about" className="pt-28">
            {/* Hero */}
            <section className="relative py-24 lg:py-32 bg-navy">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-6">
                        <p className="overline">Our Story</p>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-medium leading-[1.05] mt-6 tracking-tight">
                            Travel, told <em className="italic text-gold">slowly.</em>
                        </h1>
                        <p className="font-sans text-base lg:text-lg text-white/70 mt-8 leading-relaxed">
                            Midasis Fly Away began in a small Hyderabad studio in 2013 with a simple idea —
                            that a holiday should feel like a chapter, not a checklist. A decade on, we design journeys
                            for travelers who ask for more: a private balcony in Santorini at sunset, a quiet tea
                            ceremony in Gion, a guide in the Masai Mara who knows the lion by name.
                        </p>
                        <p className="font-sans text-base text-white/70 mt-5 leading-relaxed">
                            We're not a booking engine. We're 28 people who answer their own phones, who have walked
                            every route we sell, and who still get letters of thanks — addressed by first name.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link
                                to="/packages"
                                data-testid="about-cta-packages"
                                className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-7 py-3.5 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                            >
                                See our signature trips <ArrowRight size={14} weight="bold" />
                            </Link>
                            <Link
                                to="/contact"
                                data-testid="about-cta-contact"
                                className="inline-flex items-center gap-2 border border-white/25 hover:border-gold hover:text-gold px-7 py-3.5 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                            >
                                Meet the team
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-6 relative">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.98 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1 }}
                            className="relative aspect-[4/5] overflow-hidden border border-white/10"
                        >
                            <img
                                src="https://images.unsplash.com/photo-1768463852068-ffe4ba657840?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjB0cmF2ZWwlMjBkZXN0aW5hdGlvbiUyMGdvbGRlbiUyMGhvdXJ8ZW58MHx8fHwxNzc2NDExMTkyfDA&ixlib=rb-4.1.0&q=85"
                                alt="Luxury travel"
                                className="absolute inset-0 w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                            <div className="absolute bottom-6 left-6 right-6">
                                <p className="overline">Est. 2013 · Hyderabad</p>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Stats */}
            <section className="py-16 bg-[#080E20] border-y border-white/5" data-testid="about-stats">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {[
                            { k: "12+", v: "Years of expertise" },
                            { k: "4,800+", v: "Happy travelers" },
                            { k: "60+", v: "Destinations" },
                            { k: "28", v: "Team members" },
                        ].map((s, i) => (
                            <motion.div
                                key={s.v}
                                initial={{ opacity: 0, y: 10 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.07 }}
                            >
                                <p className="font-serif text-4xl lg:text-5xl text-gold">{s.k}</p>
                                <p className="font-sans text-xs uppercase tracking-widest text-white/55 mt-2">{s.v}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Mission */}
            <section className="py-24 lg:py-32 bg-[#080E20]" data-testid="about-mission">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <p className="overline">Our Mission</p>
                        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight">
                            To make people <em className="italic text-gold">feel</em> a place, not just visit it.
                        </h2>
                    </div>
                    <div className="lg:col-span-7">
                        <p className="font-sans text-base lg:text-lg text-white/70 leading-relaxed">
                            Great travel is personal. We spend time — sometimes hours — understanding how you want
                            to feel on your trip. Quiet, adventurous, nostalgic, newly in love. Then we build around
                            that, choosing hotels with real character, guides with real curiosity, and rhythms that
                            leave room for surprise.
                        </p>
                        <div className="grid sm:grid-cols-2 gap-6 mt-12 border-t border-l border-white/10">
                            {VALUES.map((v) => {
                                const Icon = v.icon;
                                return (
                                    <div key={v.title} className="p-8 border-r border-b border-white/10">
                                        <Icon size={28} weight="light" className="text-gold" />
                                        <h3 className="font-serif text-xl mt-4">{v.title}</h3>
                                        <p className="font-sans text-sm text-white/60 mt-2">{v.text}</p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </section>

            {/* Timeline */}
            <section className="py-24 lg:py-32 bg-navy" data-testid="about-timeline">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="grid lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <p className="overline">Our Journey</p>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight">
                                A decade of <em className="italic text-gold">milestones.</em>
                            </h2>
                            <p className="font-sans text-sm text-white/60 mt-6 leading-relaxed">
                                From a three-person studio to a team of 28 — every year has been shaped by the travelers who trusted us.
                            </p>
                        </div>
                        <div className="lg:col-span-8">
                            <div className="relative pl-8 border-l border-white/10 space-y-10">
                                {MILESTONES.map((m, i) => (
                                    <motion.div
                                        key={m.year}
                                        initial={{ opacity: 0, x: -12 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true, amount: 0.4 }}
                                        transition={{ duration: 0.6, delay: i * 0.07 }}
                                        className="relative"
                                    >
                                        <div className="absolute -left-[2.15rem] top-1 w-3 h-3 rounded-full bg-gold border-2 border-navy" />
                                        <p className="overline text-gold">{m.year}</p>
                                        <p className="font-sans text-base text-white/75 mt-2 leading-relaxed">{m.event}</p>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="py-24 lg:py-32 bg-navy" data-testid="about-cta">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-24">
                    <div className="border border-white/10 p-10 lg:p-16 grid lg:grid-cols-12 gap-10 items-center">
                        <div className="lg:col-span-8">
                            <p className="overline">Ready to travel?</p>
                            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium mt-4 leading-tight">
                                Let's build your next <em className="italic text-gold">chapter together.</em>
                            </h2>
                            <p className="font-sans text-base text-white/65 mt-5 leading-relaxed max-w-xl">
                                A 15-minute call is all it takes. Tell us where you want to go — or just how you want to feel — and we'll take it from there.
                            </p>
                        </div>
                        <div className="lg:col-span-4 flex flex-col gap-4">
                            <a
                                href={waLink("Hi! I'd like to start planning a trip with Midasis.")}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center justify-center gap-2 bg-gold text-navy hover:bg-[#E5C158] px-7 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                            >
                                Chat on WhatsApp <ArrowRight size={14} weight="bold" />
                            </a>
                            <Link
                                to="/contact"
                                className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-gold hover:text-gold px-7 py-4 font-sans font-medium uppercase tracking-wider text-xs transition-colors"
                            >
                                Send an inquiry
                            </Link>
                            <div className="flex items-center gap-3 pt-2">
                                <Phone size={16} weight="light" className="text-gold" />
                                <a href={`tel:+${SITE.whatsappRaw}`} className="font-sans text-sm text-white/60 hover:text-gold transition-colors">{SITE.whatsappDisplay}</a>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Newsletter />
        </div>
    );
}
